import React from "react"
import axios from "axios"
import moment from "moment"
import { withTranslation, WithTranslation, Trans } from "react-i18next"
import createSnapguestTheme from "./styles/snapguestTheme"
import { ThemeProvider } from "@material-ui/core/styles"
import { Stripe, loadStripe } from "@stripe/stripe-js"
import SearchBar from "./components/SearchBar"
import SearchResults from "./components/SearchResults"
import { apiUrl } from "./utils/Config"
import { observer, inject } from "mobx-react"
import Store from "./stores/Store"
import BookingDetails from "./components/BookingDetails"
import HolderForm from "./components/HolderForm"
import Button from "@material-ui/core/Button"
import Grid from "@material-ui/core/Grid"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"

/**
 * URL parameters:
 * rno* (100002)
 * channel* (code, NS)
 * color (hex code, 0874D8)
 * lang (en, sl)
 * from (2020-07-01)
 * to (2020-07-07)
 * guests (2)
 * booking (true)
 */

const search = window.location.search
const params = new URLSearchParams(search)
const rno = params.get("rno")
const channel = params.get("channel")
const color = params.get("color")

const pBooking = params.get("booking")
const pFrom = params.get("from")
const pTo = params.get("to")
const pGuests = params.get("guests")

var stripe: Stripe | null = null
loadStripe("pk_live_Mfaguv2eZdFViDOIDIM4pAI100eV3rwUhF").then((s) => {
    console.log("Stripe loaded successfully")
    stripe = s
})

const theme = createSnapguestTheme(color)

interface WidgetProps extends WithTranslation {
    store?: Store
}

@inject("store")
@observer
class Widget extends React.Component<WidgetProps> {
    constructor (props: WidgetProps) {
        super(props)

        if (pBooking) {
            this.props.store!.setBooking(pBooking === "true")
        }

        if (pGuests) {
            this.props.store!.setGuests(parseInt(pGuests))
        }

        if (pFrom && moment(pFrom).isValid()) {
            this.props.store!.setCheckIn(moment(pFrom).toDate())
        }

        if (pTo && moment(pTo).isValid()) {
            this.props.store!.setCheckOut(moment(pTo).toDate())
        }

        if (rno === null || channel === null) {
            this.props.store!.setStep(-1)
        } else {
            this.props.store!.rno = rno
            this.props.store!.channel = channel
        }
    }

    componentDidMount () {
        if (this.props.store!.step !== -1) {
            axios.get(apiUrl + "/", {
                params: {
                    rno: rno,
                    channel: channel
                }
            }).then((response) => {
                this.props.store!.setStep(response.data.status ? 0 : -1)
                this.props.store!.setFeePercent(response.data.feePercent)
                if (response.data.status) {
                    this.props.store!.getAvailability()
                }
            }).catch((error) => {
                console.error(error)
                this.props.store!.setStep(-1)
            })
        }
    }

    bookAction = () => {
        this.props.store!.stepNext()
    }

    backAction = () => {
        this.props.store!.stepBack()
    }

    payAction = () => {
        axios.post(apiUrl + "/reservation", {
            checkIn: moment(this.props.store!.checkIn).format("Y-MM-DD"),
            checkOut: moment(this.props.store!.checkOut).format("Y-MM-DD"),
            guests: this.props.store!.guests,
            bookedItemIds: this.props.store!.selectedRoomsIds,
            holder: this.props.store!.holderData
        }, {
            params: {
                rno: rno,
                channel: channel
            }
        }).then((response) => {
            if (response.data.status) {
                this.props.store!.stepNext()

                if (stripe) {
                    stripe
                        .redirectToCheckout({
                            sessionId: response.data.sessionId
                        })
                        .then((result) => {
                            if (result.error) {
                                console.error(result.error.message)
                            }
                        })
                }
            } else {
                alert("Something went wrong.")
            }
        }).catch((error) => {
            console.error(error)
        })
    }

    getStepContent (step: number) {
        const guestCount = this.props.store!.guests
        switch (step) {
        case -1:
            return (
                <Grid container justify="center">
                    <Grid item xs="auto">
                        <Typography variant="body1">{this.props.t("RNO or channel not set")}</Typography>
                    </Grid>
                </Grid>
            )
        case 0:
            return (
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <SearchBar/>
                    </Grid>
                    <Grid item xs={12}>
                        <SearchResults/>
                    </Grid>
                    {this.props.store?.booking && (
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={6}>
                                    <Typography variant="body1" color="textSecondary">
                                        <Trans i18nKey="selectRoomGuests" count={guestCount}>
                                            Select rooms for {{ guestCount }} guests.
                                        </Trans>
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Grid container justify="flex-end">
                                        <Grid item xs="auto">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={this.bookAction}
                                                disabled={!this.props.store!.canBook}
                                            >{this.props.t("Next")}</Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            )
        case 1:
            return (
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <BookingDetails/>
                    </Grid>
                    <Grid item xs={12}>
                        <HolderForm/>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item xs={6}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={this.backAction}
                                >{this.props.t("Back")}</Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container justify="flex-end">
                                    <Grid item xs="auto">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={this.payAction}
                                            disabled={!this.props.store!.canPay}
                                        >
                                            <Trans i18nKey="Pay to confirm booking" count={this.props.store!.feePercent}>
                                                Pay to confirm booking
                                            </Trans>
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            )
        case 2:
            return (
                <Grid container justify="center">
                    <Grid item xs="auto">
                        <Typography variant="body1">{this.props.t("Redirecting to payment...")}</Typography>
                    </Grid>
                </Grid>
            )
        }
    }

    render () {
        return (
            <Box className="App">
                <ThemeProvider theme={theme}>
                    {
                        this.getStepContent(this.props.store!.step)
                    }
                </ThemeProvider>
            </Box>
        )
    }
}

export default withTranslation()(Widget)
